import { useMemo } from "react";

import { JobsIcon } from "../../../assets/icons/interface/interface";
import { useRecommendedJobs, useUserAccount } from "../../../services/create-resume/queries";
import JobListingItem from "../../JobsPage/components/JobListingItem";
import PreferencesUpsellCard from "./PreferencesUpsellCard";

interface RecommendedJobsProps {
	setShowPreferencesModal: (show: boolean) => void;
	setSelectedJob: (job: unknown) => void;
	setShowJobDetailsModal: (show: boolean) => void;
}

interface EmptyStateProps {
	title: string;
	body: string;
	actionLabel: string;
	onAction: () => void;
}

function EmptyState({ title, body, actionLabel, onAction }: EmptyStateProps) {
	return (
		<div className="border border-gray-200 rounded-lg p-8 flex flex-col items-center text-center">
			<div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-3">
				<JobsIcon className="w-5 h-5 text-primary-600" />
			</div>
			<h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
			<p className="text-sm text-gray-600 mb-4 max-w-md">{body}</p>
			<button
				type="button"
				onClick={onAction}
				className="inline-flex items-center justify-center bg-primary-600 text-white px-4 h-9 rounded hover:bg-primary-700 transition-colors text-sm font-medium"
			>
				{actionLabel}
			</button>
		</div>
	);
}

function RecommendedJobs({ setShowPreferencesModal, setSelectedJob, setShowJobDetailsModal }: RecommendedJobsProps) {
	const { data: account, isLoading: isLoadingAccount } = useUserAccount();

	const areasOfExpertise = account?.jobPreferences?.areasOfExpertise;
	const { data: recommendedJobs, isLoading: isLoadingRecommendedJobs } = useRecommendedJobs(areasOfExpertise);

	// Never list a job with a missing company — skip it entirely.
	const jobs = useMemo(() => (recommendedJobs ?? []).filter((job) => Boolean(job?.company)), [recommendedJobs]);

	if (isLoadingAccount || isLoadingRecommendedJobs) {
		return (
			<div className="animate-pulse">
				{[...new Array(5)].map((_, index) => (
					<div className="h-24 bg-gray-900 opacity-10 mb-3 rounded-md" key={index} />
				))}
			</div>
		);
	}

	const progress = account?.job_preference_progress_status;
	const completenessPct = progress?.completeness_pct ?? 0;

	// Nothing filled in at all — no expertise means no recommendations are possible, so the upsell
	// takes over the whole slot.
	if (completenessPct === 0) {
		return (
			<PreferencesUpsellCard
				variant="empty"
				completenessPct={completenessPct}
				onOpenPreferences={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// Picks are scored against areas of expertise, so with none set useRecommendedJobs never fires and
	// this slot would render as a blank panel. Say why, rather than leaving the user to guess.
	if (!areasOfExpertise?.length) {
		return (
			<EmptyState
				title="No job picks yet"
				body="We match job picks to your areas of expertise, and yours are not set yet. Add them and your recommendations will show up here."
				actionLabel="Add your areas of expertise"
				onAction={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// Expertise is set but nothing came back — a real result, not a gap in the profile.
	if (jobs.length === 0) {
		return (
			<EmptyState
				title="No matches right now"
				body="Nothing on the job board matches your preferences at the moment. Widening them — more role types, more locations — usually turns up more."
				actionLabel="Edit preferences"
				onAction={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// The partially-complete nudge lives at the top of the profile page now, not above this list.
	return (
		<div className="divide-y divide-gray-200">
			{jobs.map((job, index) => (
				<JobListingItem
					key={index}
					job={{
						...job,
						logo: job.company.logo_url,
					}}
					onApply={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					onClick={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					showCompany={true}
					titleClassName="text-base"
					showMatchScore
				/>
			))}
		</div>
	);
}

export default RecommendedJobs;
